import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { portalDb } from '../utils/portalFirebase';
import { Search, User, Check, X, Share2, Building2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import debounce from 'lodash/debounce';

// Helper function to normalize text for searching by removing special characters
const normalizeForSearch = (text) => {
  if (!text) return '';
  return text.toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
    .trim();
};

const ClientSearch = ({ onClientSelect, selectedClients = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [searchComplete, setSearchComplete] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [allClients, setAllClients] = useState([]); // Store all clients
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const searchContainerRef = useRef(null);
  const searchAbortControllerRef = useRef(null);

  // Load all clients once when component mounts
  useEffect(() => {
    const loadAllClients = async () => {
      try {
        setIsLoading(true);
        const usersRef = collection(portalDb, 'users');
        const snapshot = await getDocs(usersRef);
        
        const clients = [];
        snapshot.forEach(doc => {
          const data = doc.data();
          clients.push({
            id: doc.id,
            ...data,
            // Pre-compute normalized values for faster searching
            normalizedFirstName: normalizeForSearch(data.firstName),
            normalizedLastName: normalizeForSearch(data.lastName),
            normalizedCompanyName: normalizeForSearch(data.companyName),
            companyNoSpecialChars: data.companyName ? data.companyName.toLowerCase().replace(/[^a-z0-9]/g, '') : ''
          });
        });
        
        console.log(`Loaded ${clients.length} total clients`);
        setAllClients(clients);
        setClientsLoaded(true);
      } catch (error) {
        console.error('Error loading clients:', error);
        toast.error('Error loading clients');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadAllClients();
  }, []);

  const cancelOngoingSearch = () => {
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
      searchAbortControllerRef.current = null;
    }
  };

  const resetSearchStates = (clearTerm = false) => {
    setIsLoading(false);
    setSearchComplete(false);
    setShowResults(false);
    
    if (clearTerm) {
      setSearchTerm('');
      setSearchResults([]);
      setIsSearchPerformed(false);
    }
  };

  const clearSearch = () => {
    resetSearchStates(true);
  };

  const handleSearch = async (term) => {
    if (!term || !term.trim()) {
      setSearchResults([]);
      setIsSearchPerformed(false);
      setSearchComplete(false);
      return;
    }

    // Cancel any ongoing search
    cancelOngoingSearch();

    // Create a new AbortController for this search
    searchAbortControllerRef.current = new AbortController();
    const signal = searchAbortControllerRef.current.signal;

    setIsLoading(true);
    setIsSearchPerformed(true);
    setSearchComplete(false);
    setShowResults(true);

    try {
      // Check if search was aborted
      if (signal.aborted) return;
      
      if (!clientsLoaded) {
        console.log('Clients not loaded yet, waiting...');
        // Wait for clients to load if they haven't yet
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (clientsLoaded) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });
      }
      
      // Search is now entirely client-side since we have all clients loaded
      const searchTermLower = term.toLowerCase().trim();
      const normalizedSearchTerm = normalizeForSearch(term);
      
      console.log('Searching for:', searchTermLower);
      
      // Filter clients based on search term
      const results = allClients.filter(client => {
        // Check all relevant fields
        return (
          // Name matches
          (client.firstName && client.firstName.toLowerCase().includes(searchTermLower)) ||
          (client.lastName && client.lastName.toLowerCase().includes(searchTermLower)) ||
          // Email match
          (client.email && client.email.toLowerCase().includes(searchTermLower)) ||
          // Company matches - try multiple approaches for better matching
          (client.companyName && client.companyName.toLowerCase().includes(searchTermLower)) ||
          // Try normalized company name (removes all special chars)
          (client.normalizedCompanyName && client.normalizedCompanyName.includes(normalizedSearchTerm)) ||
          // For RE/MAX type cases - check company name with slashes removed
          (client.companyNoSpecialChars && client.companyNoSpecialChars.includes(searchTermLower.replace(/[^a-z0-9]/g, '')))
        );
      }).map(client => {
        // Calculate relevance score for sorting
        let relevanceScore = 0;
        
        // Email match
        if (client.email && client.email.toLowerCase() === searchTermLower) {
          relevanceScore += 100; // Exact match
        } else if (client.email && client.email.toLowerCase().includes(searchTermLower)) {
          relevanceScore += 50; // Partial match
        }
        
        // Name matches
        if (client.firstName && client.firstName.toLowerCase() === searchTermLower) {
          relevanceScore += 80; // Exact match
        } else if (client.firstName && client.firstName.toLowerCase().includes(searchTermLower)) {
          relevanceScore += 40; // Partial match
        }
        
        if (client.lastName && client.lastName.toLowerCase() === searchTermLower) {
          relevanceScore += 80; // Exact match
        } else if (client.lastName && client.lastName.toLowerCase().includes(searchTermLower)) {
          relevanceScore += 40; // Partial match
        }
        
        // Company matches
        if (client.companyName && client.companyName.toLowerCase() === searchTermLower) {
          relevanceScore += 70; // Exact match
        } else if (client.companyName && client.companyName.toLowerCase().includes(searchTermLower)) {
          relevanceScore += 30; // Partial match
        }
        
        // For special characters in company names (like RE/MAX)
        if (client.companyNoSpecialChars && client.companyNoSpecialChars.includes(searchTermLower.replace(/[^a-z0-9]/g, ''))) {
          relevanceScore += 60; // Special match
        }
        
        return {
          ...client,
          relevanceScore
        };
      }).sort((a, b) => {
        // Sort by relevance score
        const scoreCompare = b.relevanceScore - a.relevanceScore;
        if (scoreCompare !== 0) return scoreCompare;
        
        // If same score, sort alphabetically by name
        return `${a.firstName || ''} ${a.lastName || ''}`.localeCompare(`${b.firstName || ''} ${b.lastName || ''}`);
      });
      
      console.log(`Found ${results.length} matching clients`);
      
      // Update results
      setSearchResults(results);
    } catch (error) {
      // Only show error if not aborted
      if (!signal.aborted) {
        console.error('Error searching for clients:', error);
        toast.error('Error searching for clients');
        setSearchResults([]);
      }
    } finally {
      // Only update states if not aborted
      if (!signal.aborted) {
        setIsLoading(false);
        setSearchComplete(true);
      }
    }
  };

  // Create a debounced version of the search function
  const debouncedSearch = useCallback(
    debounce((term) => {
      handleSearch(term);
    }, 300),
    [allClients, clientsLoaded] // Re-create when clients change
  );

  // Trigger search when search term changes
  useEffect(() => {
    if (searchTerm.trim().length > 0) {
      setIsLoading(true);
      setSearchComplete(false);
      debouncedSearch(searchTerm);
    } else if (searchTerm.trim().length === 0) {
      cancelOngoingSearch();
      debouncedSearch.cancel();
      setSearchResults([]);
      setIsSearchPerformed(false);
      setSearchComplete(false);
      setShowResults(false);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Handle clicks outside of the search component
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        resetSearchStates();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cancel ongoing search when component unmounts
      cancelOngoingSearch();
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSelectClient = (client) => {
    // Check if this client is already selected
    const isAlreadySelected = selectedClients.some(c => c.id === client.id);
    
    if (isAlreadySelected) {
      // If already selected, remove it
      onClientSelect(selectedClients.filter(c => c.id !== client.id));
    } else {
      // If not selected, add it
      onClientSelect([...selectedClients, client]);
      // Clear the search results after selection
      resetSearchStates(true);
    }
  };

  const handleFocus = () => {
    if (searchTerm.trim().length >= 2) {
      setShowResults(true);
    }
  };

  // Filter out already selected clients from search results
  const filteredResults = searchResults.filter(
    result => !selectedClients.some(client => client.id === result.id)
  );

  return (
    <div className="space-y-4" ref={searchContainerRef}>
      <div className="flex flex-col">
        <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
          <Share2 className="h-4 w-4 mr-2 text-blue-500" />
          Share with Portal Clients (Optional)
        </label>
        <p className="text-sm text-gray-500 mb-3">
          Search for clients by name, email, or company to share this report with them. Selected clients will receive access to view this report in their portal.
        </p>
        <div className="relative">
          <div className="flex">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={handleFocus}
                placeholder="Type to search clients by name, email, or company..."
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-3 py-3 sm:text-sm border border-blue-200 rounded-md bg-blue-50 shadow-sm"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
              {isLoading && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedClients.length > 0 ? (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Selected clients:</h4>
          <div className="flex flex-wrap gap-2">
            {selectedClients.map(client => (
              <div 
                key={client.id} 
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
              >
                <span>{client.firstName} {client.lastName}</span>
                <button 
                  type="button"
                  onClick={() => handleSelectClient(client)}
                  className="ml-1 text-blue-500 hover:text-blue-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : !isSearchPerformed && searchTerm.trim().length === 0 && (
        <div className="mt-4 p-4 border border-dashed border-gray-300 rounded-md bg-gray-50 text-center">
          <p className="text-gray-500">No clients selected. Start typing to search for clients.</p>
        </div>
      )}

      {showResults && isSearchPerformed && filteredResults.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Found {filteredResults.length} {filteredResults.length === 1 ? 'result' : 'results'}
          </h4>
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <ul className="divide-y divide-gray-200">
              {filteredResults.map(user => (
                <li 
                  key={user.id}
                  className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleSelectClient(user)}
                >
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="h-5 w-5 text-slate-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</p>
                      <div className="text-xs text-gray-500">
                        <p>{user.email}</p>
                        {user.companyName && (
                          <p className="flex items-center">
                            <Building2 className="h-3 w-3 mr-1 text-blue-500" />
                            <span>{user.companyName}</span>
                          </p>
                        )}
                        <p className="capitalize">{user.userType || 'User'}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="h-6 w-6 border border-gray-300 rounded-md" />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {showResults && isSearchPerformed && filteredResults.length === 0 && searchResults.length > 0 && (
        <div className="mt-4 text-gray-500 text-sm">
          All matching clients have already been selected.
        </div>
      )}

      {showResults && isLoading && searchTerm.trim().length > 0 && (
        <div className="mt-4 text-gray-500 text-sm">
          Searching...
        </div>
      )}

      {showResults && !isLoading && searchComplete && isSearchPerformed && searchResults.length === 0 && searchTerm.trim().length > 0 && (
        <div className="mt-4 text-gray-500 text-sm">
          No results found. Try a different search term.
        </div>
      )}
    </div>
  );
};

export default ClientSearch; 